<template>
  <v-container>
    <v-layout>
      <v-flex>
        <v-card>
          <v-card-title>
            <v-avatar size="36px" class="mr-2">
              <img :alt="$t('installPrompt.logo')" :src="client.logo">
            </v-avatar>
            <h3 class="title pl-2 pr-4">{{headline}}</h3>
          </v-card-title>
          <v-card-text>
            <div v-if="isIOS">
              <v-list>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>{{icons.mdiNumeric1CircleOutline}}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <h4 class="body-2 mb-4">{{ $t('installPrompt.installWebappIOSInstructions1') }}</h4>
                    <div class="img-container rounded-corner">
                      <img :src="`${baseUrl}img/install-webapp/ios-step01.jpg`" />
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>{{icons.mdiNumeric2CircleOutline}}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <h4 class="body-2 mb-4">{{ $t('installPrompt.installWebappIOSInstructions2') }}</h4>
                    <div class="img-container">
                      <img :src="`${baseUrl}img/install-webapp/ios-step02_${langIso}.jpg`" />
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>{{icons.mdiNumeric3CircleOutline}}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <h4 class="body-2 mb-4">{{ $t('installPrompt.installWebappIOSInstructions3') }}</h4>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <p class="body-2 mb-1" v-html="$t('installPrompt.installWebappMobileInstructions2', {name: client.name})"></p>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
            <div v-else-if="hasInstallPrompt">
              <div class="description" v-if="isMobile">
                <p>{{ $t('installPrompt.installWebappMobileInstructions1')}}</p>
                <p class="body-2 mb-1" v-html="$t('installPrompt.installWebappMobileInstructions2', {name: client.name})"></p>
              </div>
              <div class="description" v-else>
                <p>{{ $t('installPrompt.installWebappDesktopInstructions1')}}</p>
                <p class="body-2 mb-1" v-html="$t('installPrompt.installWebappDesktopInstructions2', {name: client.name})"></p>
              </div>
            </div>
            <div v-else>
              <div class="description">
                <p>{{ $t('installPrompt.installWebappNotAvailableText')}}</p>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="text-right" v-if="hasInstallPrompt">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="install">{{buttonText}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
/*
  this only works on Chrome (Mobile/Desktop) and Safari (iOS)!

  Chrome:
  browser listens for "installbeforeprompt"-Event
  (not thrown on any other device / browser or when webapp is already installed)
  in main.js this event gets preventDefault() to prevent a
  native add-to-homescreen bottom-bar on android from showing.

  clicking the "install" button opens the native add-to-homescreen dialog
  submitting the dialog installs a natively-looking app (on mobile) or browser-app (on desktop)

  this box is shown under User-profile-box, but moves further down to rewards when
  dismissed (click on x)
  (see @/views/Home.vue)

  Safari:
  User has to manually execute "add to homescreen"
*/
  import _ from 'lodash';
  import {mdiNumeric1CircleOutline, mdiNumeric2CircleOutline, mdiNumeric3CircleOutline} from '@mdi/js';
  import {storage} from '@/store';
  import {mapGetters} from 'vuex';

  export default {
    name: 'InstallWebappPopup',
    props: ['data'],
    data() {
      return {
        promotionStorage: JSON.parse(storage.getItem('promotion')),
        installPrompt: null,
        hasInstallPrompt: false,
        icons: {
          mdiNumeric1CircleOutline, mdiNumeric2CircleOutline, mdiNumeric3CircleOutline
        }
      }
    },
    computed: {
      ...mapGetters(['client', 'device', 'installPWAEvent']),
      baseUrl() {
        return process.env.BASE_URL;
      },
      isIOS() {
        return _.get(this.device, 'os.name').toLowerCase() === 'ios' || _.get(this.device, 'os.name').toLowerCase() === 'ipados';
      },
      isMobile() {
        return _.get(this.device, 'device.type') === 'smartphone';
      },
      langIso() {
        return (this.$root.$i18n.locale.toLowerCase() === 'de') ? 'de' : 'en';
      },
      headline() {
        if (!this.isIOS && !this.hasInstallPrompt) {
          return this.$t('installPrompt.installWebappNotAvailableHeadline');
        }
        if (this.isMobile || this.isIOS) {
          return this.$t('installPrompt.installWebappHeadlineMobile');
        }
        return this.$t('installPrompt.installWebappHeadlineDesktop');
      },
      buttonText() {
        if (this.isMobile) {
          return this.$t('installPrompt.installWebappButtonMobile');
        }
        return this.$t('installPrompt.installWebappButtonDesktop');
      }
    },
    mounted() {
      // show Box only when event from "beforeInstallPrompt"-Listener is available
      // works only on chrome and when webapp is not installed
      if (this.installPWAEvent) {
        this.hasInstallPrompt = true;
        this.installPrompt = this.installPWAEvent;
      }

      // Chrome (desktop): detects if Browser-Tab is extracted as PWA
      window.matchMedia('(display-mode: standalone)').addEventListener("change", () => {
        this.$router.push({name: 'home'});
      });

    },
    methods: {
      afterLeave() {
        this.promotionStorage = JSON.parse(storage.getItem('promotion'));
        storage.setItem('promotion', JSON.stringify({discreetWebappInstaller: true, ...this.promotionStorage}));
        this.$emit('setInstallPopupPosition');
      },
      install() {
        /*
         * beforeInstallPrompt-event is reset to local variable
         * if a user has dismissed the native install-dialog but clicks
         * the install-button on this box again the prompt needs to be
         * retriggerd to open the native install-dialog again
         */
        this.installPrompt = this.installPWAEvent; // retrigger

        if (this.installPrompt) {
          // calls native install-dialog
          this.installPrompt.prompt();
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .v-card::v-deep .v-card__title {
    word-break: normal;
    flex-wrap: nowrap;
    align-items: center;
    h3 {
      line-height: 1.3em;
    }
  }
  .v-card::v-deep .v-card__actions {
    padding-top: 0;
  }
  img {
    width: 100%;
    display: grid;
  }
  .v-list-item {
    margin-bottom: 2em;
    &:before {
      content: none;
    }
  }
  .v-list-item__content {
    overflow: visible;
  }
  .img-container {
    box-shadow: 0 2px 10px 4px rgba(0,0,0,.1);
    overflow: hidden;
    &.rounded-corner {
      border-radius: 0 0 35px 35px;
    }
  }
</style>
